/**
 * CHORUS - Multi-AI Chat Application
 * Main Application Component
 */

import { store, toggleSidebar, toggleParallelMode, createConversation, setCurrentConversation, deleteConversation as deleteConv, addMessage, updateMessage, setActiveTab, addAgentMessage, updateAgentMessage, closeArtifactPanel, toggleKBPanel, addAttachedFile, removeAttachedFile, clearAttachedFiles, setFollowUps, setRoutingSuggestion, setMemorySuggestion, setUsageData, updateSettings } from '../lib/appState.js';
import { sendMessage, sendParallel, stopStream, sendAgentMessage, loadAgentHistory, loadMoreAgentHistory, startAgentPolling, stopAgentPolling, updateRoutingSuggestionForInput } from '../lib/chat.js';
import { renderMarkdown, handleCodeCopy } from '../lib/markdown.js';
import { readAgentFile } from '../lib/api.js';
import { renderSettings, bindSettingsEvents } from './Settings.js';
import { renderApprovalQueue, bindApprovalEvents } from './ApprovalQueue.js';
import { renderMissionControl, initMissionControl, cleanupMissionControl } from './MissionControl.js';
import { openVoiceMode, closeVoiceMode } from './VoiceMode.js';
import { signOut } from '../lib/auth.js';
import { synthesizeResponses } from '../lib/synthesizer.js';
import { handleCanvasAction, renderCanvasPanel, setupCanvasResize } from './Canvas.js';
import { getArtifactsForMessage } from '../lib/artifacts.js';
import { providers } from '../providers/registry.js';
import { voice } from '../voice/voice.js';
import { routeInput, getCommandHelp } from '../lib/router.js';
import { db } from '../lib/db.js';
import { showToast } from '../lib/toast.js';
import { processFile, removeDocument, toggleDocumentEnabled, isAcceptedFile, getAcceptString } from '../lib/documents.js';
import { addMemoryItem } from '../lib/memory.js';
import { formatTokens, formatCost, getConversationUsage } from '../lib/usage.js';

const icons = {
  menu: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="3" y1="12" x2="21" y2="12"></line><line x1="3" y1="6" x2="21" y2="6"></line><line x1="3" y1="18" x2="21" y2="18"></line></svg>`,
  plus: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>`,
  send: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>`,
  stop: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="6" y="6" width="12" height="12" rx="2"></rect></svg>`,
  message: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>`,
  sparkles: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"></path><path d="M5 3v4"></path><path d="M3 5h4"></path><path d="M19 17v4"></path><path d="M17 19h4"></path></svg>`,
  settings: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path></svg>`,
  x: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>`,
  trash: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>`,
  mic: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path><path d="M19 10v2a7 7 0 0 1-14 0v-2"></path><line x1="12" y1="19" x2="12" y2="23"></line><line x1="8" y1="23" x2="16" y2="23"></line></svg>`,
  search: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>`,
  pin: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 17v5"/><path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z"/></svg>`,
  dots: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="5" r="1"/><circle cx="12" cy="12" r="1"/><circle cx="12" cy="19" r="1"/></svg>`,
  edit: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>`,
  copy: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>`,
  refresh: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>`,
  paperclip: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>`,
  file: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>`,
  database: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/></svg>`,
  chevronDown: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>`,
  chevronRight: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>`,
  upload: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/><path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/></svg>`,
  globe: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>`,
  brain: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96.44 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24 2.5 2.5 0 0 1 1.98-3A2.5 2.5 0 0 1 9.5 2Z"/><path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96.44 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24 2.5 2.5 0 0 0-1.98-3A2.5 2.5 0 0 0 14.5 2Z"/></svg>`,
  download: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>`,
  share: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>`,
  zap: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>`,
  externalLink: `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>`,
};

let settingsOpen = false;
let voiceActive = false;
let eventsBound = false;
let voiceUnsubscribe = null;
let sidebarSearchQuery = '';
const tabInputValues = {}; // per-tab input text storage

/**
 * Render the application shell
 */
export function renderApp(container) {
  const state = store.getState();
  const activeTab = state.ui.activeTab;

  // Preserve current input value before re-render destroys the textarea
  const currentTextarea = container.querySelector('#message-input');
  if (currentTextarea) {
    tabInputValues[activeTab] = currentTextarea.value;
  }

  const savedInput = tabInputValues[activeTab] || '';

  container.innerHTML = `
    <div class="app-background"></div>

    <div class="app-layout">
      <div class="sidebar-backdrop ${state.ui.sidebarOpen ? 'visible' : ''}" data-action="close-sidebar"></div>

      <aside class="sidebar ${state.ui.sidebarOpen ? 'open' : ''}" id="sidebar">
        <div class="sidebar-header">
          <div class="sidebar-logo">
            <div class="sidebar-logo-icon">${icons.sparkles}</div>
            <span class="sidebar-logo-text">Chorus</span>
          </div>
          <button class="menu-btn" data-action="close-sidebar" aria-label="Close sidebar">${icons.x}</button>
        </div>

        <button class="new-chat-btn" data-action="new-chat">
          ${icons.plus}
          <span>New Chat</span>
        </button>

        <div class="sidebar-search">
          <div class="sidebar-search-wrapper">
            ${icons.search}
            <input type="text" class="sidebar-search-input" id="sidebar-search" placeholder="Search conversations..." value="${escapeHtml(sidebarSearchQuery)}">
          </div>
        </div>

        <div class="sidebar-content">
          <div class="conversation-list" id="conversation-list">
            ${renderConversationList(state)}
          </div>

          ${renderKBPanel(state)}
        </div>

        <div class="sidebar-footer">
          <button class="menu-btn" data-action="settings" aria-label="Settings">${icons.settings}</button>
        </div>
      </aside>

      <main class="main-content">
        <header class="header">
          <div class="header-left">
            <button class="menu-btn" data-action="toggle-sidebar" aria-label="Toggle sidebar">${icons.menu}</button>
          </div>
          <div class="header-center">
            <span class="header-title">Chorus</span>
          </div>
          <div class="header-right">
            <button class="menu-btn" data-action="settings" aria-label="Settings">${icons.settings}</button>
          </div>
        </header>

        ${renderTabBar(state)}

        <div class="chat-area" id="chat-area">
          ${state.ui.activeTab === 'chat'
            ? renderChatArea(state)
            : state.ui.activeTab === 'mission-control'
            ? renderMissionControl()
            : renderAgentChatArea(state)}
        </div>

        <button class="scroll-to-bottom-btn" id="scroll-to-bottom" data-action="scroll-to-bottom" aria-label="Scroll to bottom" style="display:none">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 5v14M5 12l7 7 7-7"/></svg>
        </button>

        <div class="drop-overlay" id="drop-overlay">
          <div class="drop-overlay-content">
            ${icons.upload}
            <span>Drop files here</span>
            <span class="drop-overlay-hint">.txt, .md, .json, .csv, .pdf</span>
          </div>
        </div>

        <div class="input-area" ${activeTab === 'mission-control' ? 'style="display:none"' : ''}>
          <div class="routing-suggestion-container">${renderRoutingSuggestion(state)}</div>
          ${renderMemorySuggestion(state)}
          ${renderFollowUpSuggestions(state)}
          <div class="input-container">
            ${renderAttachedFiles(state)}
            <div class="input-wrapper">
              <div class="input-toolbar">
                ${state.ui.activeTab === 'chat' ? `
                  <button class="parallel-toggle-compact ${state.parallelMode.enabled ? 'active' : ''}" data-action="toggle-parallel" aria-label="Toggle parallel mode" title="Parallel Mode">
                    ${icons.parallel || '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M16 3h5v5M4 20L21 3M21 16v5h-5M15 15l6 6M4 4l5 5"/></svg>'}
                  </button>
                  <button class="search-toggle ${state.settings.searchEnabled ? 'active' : ''}" data-action="toggle-search" aria-label="Toggle web search" title="Web Search">
                    ${icons.globe}
                  </button>
                ` : ''}
                <button class="attach-btn" data-action="attach-file" aria-label="Attach file" title="Attach file">
                  ${icons.paperclip}
                </button>
                <input type="file" id="file-input" class="file-input-hidden" accept="${getAcceptString()}" multiple>
                ${voice.isSupported() ? `
                  <button class="voice-btn ${voiceActive ? 'recording' : ''}" data-action="toggle-voice" aria-label="Voice input" title="Push to talk">
                    ${icons.mic}
                  </button>
                  <button class="voice-mode-btn" data-action="open-voice-mode" aria-label="Voice mode" title="Voice Mode (Car Mode)">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><circle cx="12" cy="12" r="10" stroke-dasharray="4 4" opacity="0.3"/></svg>
                  </button>
                ` : ''}
                <div class="toolbar-spacer"></div>
                ${state.ui.activeTab === 'chat' ? (state.parallelMode.enabled ? '' : renderActiveProvider()) : `<div class="active-provider"><span class="provider-dot configured"></span><span class="agent-label">${getAgentName(state.ui.activeTab)}</span></div>`}
              </div>
              <div class="input-row">
                <textarea
                  class="input-field"
                  id="message-input"
                  placeholder="Message Chorus..."
                  rows="1"
                ></textarea>
                ${state.chat.isStreaming
                  ? `<button class="send-btn stop-btn" data-action="stop" aria-label="Stop generating">${icons.stop}</button>`
                  : `<button class="send-btn" data-action="send" aria-label="Send message" disabled>${icons.send}</button>`
                }
              </div>
              ${state.ui.activeTab === 'chat' && state.parallelMode.enabled ? renderModelChips(state) : ''}
              ${renderTokenCounter(state)}
            </div>
          </div>
        </div>
      </main>
    </div>

    ${settingsOpen ? renderSettings() : ''}
    ${renderApprovalQueue()}
  `;

  bindEvents(container);
  const textarea = container.querySelector('#message-input');
  setupTextareaAutoResize(textarea);

  if (savedInput && textarea) {
    textarea.value = savedInput;
    textarea.focus();
    // Place cursor at end
    textarea.selectionStart = textarea.selectionEnd = savedInput.length;
    const sendBtn = container.querySelector('[data-action="send"]');
    if (sendBtn) sendBtn.disabled = false;
  }

  if (settingsOpen) {
    bindSettingsEvents(container);
  }

  bindApprovalEvents(container);

  // Bind Mission Control events if active
  if (state.ui.activeTab === 'mission-control') {
    // bindMissionControlEvents(container);
    initMissionControl();
  }

  requestAnimationFrame(() => {
    const chatArea = container.querySelector('#chat-area');
    if (chatArea) chatArea.scrollTop = chatArea.scrollHeight;
  });
}

function renderTabBar(state) {
  const openclawProvider = providers.get('openclaw');
  const agents = openclawProvider?.models || [];
  const activeTab = state.ui.activeTab;

  return `
    <div class="tab-bar">
      <button class="tab-btn ${activeTab === 'chat' ? 'active' : ''}" data-action="switch-tab" data-tab="chat">
        Chat
      </button>
      <button class="tab-btn ${activeTab === 'mission-control' ? 'active' : ''}" data-action="switch-tab" data-tab="mission-control">
        ◇ Mission Control
      </button>
      ${agents.map(agent => `
        <button class="tab-btn ${activeTab === agent.id ? 'active' : ''}" data-action="switch-tab" data-tab="${agent.id}">
          ${agent.name}
        </button>
      `).join('')}
    </div>
  `;
}

function renderAgentChatArea(state) {
  const agentId = state.ui.activeTab;
  const agentConv = state.agentConversations[agentId];

  if (!agentConv || agentConv.messages.length === 0) {
    const agentName = providers.get('openclaw')?.models?.find(m => m.id === agentId)?.name || agentId;
    // Check if we're currently loading history
    if (agentConv === undefined) {
      return `
        <div class="empty-state">
          <div class="empty-state-icon">${icons.sparkles}</div>
          <h2 class="empty-state-title">Loading history...</h2>
          <p class="empty-state-description">Fetching transcript for ${agentName}...</p>
        </div>
      `;
    }
    return `
      <div class="empty-state">
        <div class="empty-state-icon">${icons.sparkles}</div>
        <h2 class="empty-state-title">${agentName}</h2>
        <p class="empty-state-description">
          Chat directly with this OpenClaw agent. Type a message below to start.
        </p>
      </div>
    `;
  }

  const loadMoreBtn = agentConv.hasMore
    ? `<button class="load-more-btn" data-action="load-more-history" data-agent="${agentId}" style="display:block;margin:12px auto;padding:8px 24px;background:rgba(139,92,246,0.15);border:1px solid rgba(139,92,246,0.3);color:#a78bfa;border-radius:8px;cursor:pointer;font-size:0.85rem;">⬆ Load older messages (${agentConv.total - agentConv.offset} remaining)</button>`
    : (agentConv.historyLoaded && agentConv.messages.some(m => m.fromHistory)
      ? `<div style="text-align:center;padding:12px;color:rgba(255,255,255,0.3);font-size:0.8rem;">— Beginning of conversation —</div>`
      : '');

  return `<div class="chat-container">${loadMoreBtn}${agentConv.messages.map(msg => renderMessage(msg)).join('')}</div>`;
}

function renderActiveProvider() {
  const active = providers.getActive();
  if (!active) {
    return `<div class="active-provider"><span class="provider-dot unconfigured"></span> Connecting to server...</div>`;
  }
  const provider = providers.get(active.providerId);
  const configured = providers.getConfigured();

  return `
    <div class="active-provider">
      <span class="provider-dot configured"></span>
      <select class="provider-select-inline" data-action="change-provider">
        ${configured.map(p => `
          <option value="${p.id}" ${p.id === active.providerId ? 'selected' : ''}>${p.name}</option>
        `).join('')}
      </select>
      <select class="model-select-inline" data-action="change-model">
        ${(provider?.models || []).map(m => `
          <option value="${m.id}" ${m.id === active.model ? 'selected' : ''}>${m.name || m.id}</option>
        `).join('')}
      </select>
    </div>
  `;
}

function renderRoutingSuggestion(state) {
  const suggestion = state.routingSuggestion;
  if (!suggestion || state.parallelMode.enabled) return '';

  return `
    <div class="routing-suggestion animate-fade-in-up">
      <span class="routing-suggestion-icon">${icons.zap}</span>
      <span class="routing-suggestion-text">Suggested: <strong>${suggestion.reason}</strong></span>
      <button class="routing-suggestion-use" data-action="use-routing" data-provider="${suggestion.provider}" data-model="${suggestion.model}">Use</button>
      <button class="routing-suggestion-dismiss" data-action="dismiss-routing">${icons.x}</button>
    </div>
  `;
}

function renderMemorySuggestion(state) {
  const suggestion = state.memory.suggestion;
  if (!suggestion) return '';

  return `
    <div class="memory-suggestion animate-fade-in-up">
      <span class="memory-suggestion-icon">${icons.brain}</span>
      <span class="memory-suggestion-text">Remember: "${escapeHtml(suggestion.content)}"</span>
      <button class="memory-suggestion-save" data-action="save-memory" data-content="${escapeHtml(suggestion.content)}">Save</button>
      <button class="memory-suggestion-dismiss" data-action="dismiss-memory">${icons.x}</button>
    </div>
  `;
}

function renderFollowUpSuggestions(state) {
  if (!state.followUps || state.followUps.length === 0 || state.chat.isStreaming) return '';

  return `
    <div class="follow-up-chips">
      ${state.followUps.map(f => `
        <button class="follow-up-chip" data-action="follow-up" data-text="${escapeHtml(f.text)}">
          ${escapeHtml(f.text)}
        </button>
      `).join('')}
    </div>
  `;
}

function renderTokenCounter(state) {
  const usage = state.usage;
  if (!usage.currentConv.inputTokens && !usage.currentConv.outputTokens) return '';

  const totalTokens = usage.currentConv.inputTokens + usage.currentConv.outputTokens;
  return `
    <div class="token-counter" title="Input: ${formatTokens(usage.currentConv.inputTokens)} · Output: ${formatTokens(usage.currentConv.outputTokens)} · Est. ${formatCost(usage.currentConv.cost)}">
      <span class="token-counter-value">${formatTokens(totalTokens)} tokens</span>
      <span class="token-counter-cost">${formatCost(usage.currentConv.cost)}</span>
    </div>
  `;
}

function renderSearchResults(message) {
  if (!message.searchResults || message.searchResults.length === 0) return '';

  return `
    <div class="search-results">
      <button class="search-results-toggle" data-action="toggle-search-results" data-msg-id="${message.id}">
        ${icons.globe}
        <span>Web Sources (${message.searchResults.length})</span>
        ${icons.chevronDown}
      </button>
      <div class="search-results-list" id="search-results-${message.id}" style="display:none">
        ${message.searchResults.map(r => `
          <a class="search-result-card" href="${escapeHtml(r.url)}" target="_blank" rel="noopener noreferrer">
            <div class="search-result-title">${escapeHtml(r.title)} ${icons.externalLink}</div>
            <div class="search-result-snippet">${escapeHtml(r.snippet)}</div>
            <div class="search-result-url">${escapeHtml(r.url)}</div>
          </a>
        `).join('')}
      </div>
    </div>
  `;
}

function renderConversationList(state) {
  const activeTab = state.ui.activeTab;

  // If on an agent tab, show agent conversation history
  if (activeTab !== 'chat') {
    const agentConv = state.agentConversations[activeTab];
    if (!agentConv || agentConv.messages.length === 0) {
      return `<div class="text-center p-4 text-muted text-sm">No conversation history yet</div>`;
    }
    // Show simple message count for agent conversations
    const messageCount = agentConv.messages.filter(m => m.role === 'user').length;
    return `<div class="text-center p-4 text-muted text-sm">${messageCount} messages in this session</div>`;
  }

  // Regular chat mode - show conversation list
  if (state.conversations.length === 0) {
    return `<div class="text-center p-4 text-muted text-sm">No conversations yet</div>`;
  }

  let conversations = state.conversations;

  // Filter by search
  if (sidebarSearchQuery) {
    const q = sidebarSearchQuery.toLowerCase();
    conversations = conversations.filter(conv => {
      if (conv.title.toLowerCase().includes(q)) return true;
      return conv.messages?.some(m => m.content?.toLowerCase().includes(q));
    });
  }

  // Separate pinned and unpinned
  const pinned = conversations.filter(c => c.pinned);
  const unpinned = conversations.filter(c => !c.pinned);

  let html = '';

  if (pinned.length > 0) {
    html += `<div class="conversation-section-label">Pinned</div>`;
    html += pinned.map(conv => renderConversationItem(conv, state)).join('');
  }

  if (unpinned.length > 0) {
    if (pinned.length > 0) {
      html += `<div class="conversation-section-label">Recent</div>`;
    }
    html += unpinned.map(conv => renderConversationItem(conv, state)).join('');
  }

  if (conversations.length === 0 && sidebarSearchQuery) {
    html = `<div class="text-center p-4 text-muted text-sm">No matches found</div>`;
  }

  return html;
}

function renderConversationItem(conv, state) {
  return `
    <div class="conversation-item ${conv.id === state.chat.currentConversationId ? 'active' : ''}"
         data-action="select-conversation"
         data-id="${conv.id}">
      ${conv.pinned ? `<span class="conv-pin-icon">${icons.pin}</span>` : icons.message}
      <span class="conversation-title">${escapeHtml(conv.title)}</span>
      <div class="conversation-actions">
        <button class="conv-action-btn" data-action="pin-conversation" data-id="${conv.id}" title="${conv.pinned ? 'Unpin' : 'Pin'}">
          ${icons.pin}
        </button>
        <button class="conv-action-btn" data-action="show-conv-menu" data-id="${conv.id}" title="More">
          ${icons.dots}
        </button>
      </div>
    </div>
  `;
}

function renderChatArea(state) {
  const currentConv = state.conversations.find(c => c.id === state.chat.currentConversationId);

  if (!currentConv || currentConv.messages.length === 0) {
    return `
      <div class="empty-state">
        <div class="empty-state-icon">${icons.sparkles}</div>
        <h2 class="empty-state-title">Welcome to Chorus</h2>
        <p class="empty-state-description">
          Query multiple AI models simultaneously and compare their responses side by side.
        </p>
        <div class="quick-actions">
          <button class="quick-action-btn" data-action="quick-prompt" data-prompt="Explain quantum computing in simple terms">Explain quantum computing</button>
          <button class="quick-action-btn" data-action="quick-prompt" data-prompt="Write a Python function to find prime numbers">Write a prime finder</button>
          <button class="quick-action-btn" data-action="quick-prompt" data-prompt="Compare React, Vue, and Svelte frameworks">Compare JS frameworks</button>
        </div>
      </div>
    `;
  }

  const messages = currentConv.messages;
  const rendered = [];
  let i = 0;
  while (i < messages.length) {
    const msg = messages[i];
    if (msg.role === 'assistant') {
      // Collect consecutive assistant messages for parallel display
      const parallelGroup = [msg];
      while (i + 1 < messages.length && messages[i + 1].role === 'assistant') {
        i++;
        parallelGroup.push(messages[i]);
      }
      if (parallelGroup.length > 1) {
        rendered.push(renderParallelMessages(parallelGroup));
      } else {
        rendered.push(renderMessage(msg));
      }
    } else {
      rendered.push(renderMessage(msg));
    }
    i++;
  }

  return `<div class="chat-container">${rendered.join('')}</div>`;
}

function renderParallelMessages(messages) {
  const allComplete = messages.every(m => m.status === 'complete');
  return `
    <div class="parallel-responses">
      ${messages.map(msg => `
        <div class="parallel-response-card ${msg.status}" data-msg-id="${msg.id}">
          <div class="parallel-response-header">
            <span class="model-badge">${escapeHtml(msg.model || 'AI')}</span>
            ${msg.status === 'streaming' ? '<span class="streaming-dot"></span>' : ''}
            ${allComplete ? `<button class="pick-winner-btn" data-action="pick-winner" data-model="${escapeHtml(msg.model || '')}" title="Pick as best">Best</button>` : ''}
          </div>
          <div class="message-body markdown-body">
            ${msg.status === 'streaming' && !msg.content ? renderTypingIndicator() : renderMarkdown(msg.content)}
          </div>
        </div>
      `).join('')}
    </div>
    ${allComplete ? `
      <div class="parallel-actions">
        <button class="parallel-action-btn" data-action="synthesize">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"></path></svg>
          Synthesize Responses
        </button>
      </div>
    ` : ''}
  `;
}

function renderMessage(message) {
  const isUser = message.role === 'user';
  const isStreaming = message.status === 'streaming';
  const isError = message.status === 'error';

  const actions = isUser
    ? `<div class="msg-actions">
        <button class="msg-action-btn" data-action="edit-message" data-msg-id="${message.id}" title="Edit">${icons.edit}</button>
        <button class="msg-action-btn" data-action="copy-message" data-msg-id="${message.id}" title="Copy">${icons.copy}</button>
        <button class="msg-action-btn" data-action="delete-message" data-msg-id="${message.id}" title="Delete">${icons.trash}</button>
      </div>`
    : `<div class="msg-actions">
        <button class="msg-action-btn" data-action="copy-message" data-msg-id="${message.id}" title="Copy">${icons.copy}</button>
        <button class="msg-action-btn" data-action="regenerate-message" data-msg-id="${message.id}" title="Regenerate">${icons.refresh}</button>
        <button class="msg-action-btn" data-action="delete-message" data-msg-id="${message.id}" title="Delete">${icons.trash}</button>
      </div>`;

  return `
    <div class="message ${message.role} ${isStreaming ? 'streaming' : ''} ${isError ? 'error' : ''}" data-msg-id="${message.id}">
      <div class="message-avatar ${message.role}">
        ${isUser ? 'U' : (message.model?.[0] || 'A')}
      </div>
      <div class="message-content">
        <div class="message-bubble">
          ${!isStreaming ? actions : ''}
          <div class="message-body markdown-body">
            ${isStreaming && !message.content
              ? renderTypingIndicator()
              : isUser
                ? escapeHtml(message.content)
                : linkifyFilePaths(renderMarkdown(message.content))}
          </div>
          ${isStreaming && message.content ? '<span class="cursor-blink"></span>' : ''}
        </div>
        <div class="message-meta">
          ${message.model ? `<span class="model-badge-sm">${message.model}</span>` : ''}
          <span>${formatTime(message.timestamp)}</span>
          ${isError ? '<span class="error-badge">Error</span>' : ''}
        </div>
        ${!isUser && !isStreaming ? renderSourceCitations(message) : ''}
        ${!isUser && !isStreaming ? renderSearchResults(message) : ''}
      </div>
    </div>
  `;
}

function renderTypingIndicator() {
  return `<div class="typing-indicator"><span class="typing-dot"></span><span class="typing-dot"></span><span class="typing-dot"></span></div>`;
}

function renderKBDocItem(doc) {
  let scopeBadge = '';
  if (doc.scope === 'agent') {
    scopeBadge = `<span class="kb-scope-badge agent">Agent KB</span>`;
  } else if (doc.scope === 'conversation') {
    scopeBadge = `<span class="kb-scope-badge conversation">Conversation</span>`;
  }

  return `
    <div class="kb-doc-item">
      <div class="kb-doc-info">
        <span class="kb-doc-icon">${icons.file}</span>
        <div class="kb-doc-details">
          <span class="kb-doc-name">${escapeHtml(doc.name)} ${scopeBadge}</span>
          <span class="kb-doc-meta">${formatFileSize(doc.size)} · ${doc.chunkCount} chunks</span>
        </div>
      </div>
      <div class="kb-doc-actions">
        <label class="kb-toggle" title="${doc.enabled ? 'Disable' : 'Enable'}">
          <input type="checkbox" ${doc.enabled ? 'checked' : ''} data-action="toggle-kb-doc" data-doc-id="${doc.id}">
          <span class="kb-toggle-switch"></span>
        </label>
        <button class="kb-doc-delete" data-action="delete-kb-doc" data-doc-id="${doc.id}" title="Remove">${icons.x}</button>
      </div>
    </div>
  `;
}

function renderKBPanel(state) {
  const kb = state.knowledgeBase;
  const isOpen = kb.panelOpen;

  // Filter documents to show based on current context
  const activeTab = state.ui.activeTab;
  const currentConvId = state.chat.currentConversationId;
  const visibleDocs = kb.documents.filter(doc => {
    if (doc.scope === 'global') return true;
    if (doc.scope === 'agent' && activeTab !== 'chat' && doc.agentId === activeTab) return true;
    if (doc.scope === 'conversation' && doc.conversationId === currentConvId) return true;
    return false;
  });

  return `
    <div class="kb-panel">
      <button class="kb-panel-header" data-action="toggle-kb-panel">
        <span class="kb-panel-header-left">
          ${icons.database}
          <span>Knowledge Base</span>
          ${visibleDocs.length > 0 ? `<span class="kb-count">${visibleDocs.length}</span>` : ''}
        </span>
        <span class="kb-chevron ${isOpen ? 'open' : ''}">${icons.chevronRight}</span>
      </button>
      ${isOpen ? `
        <div class="kb-panel-body">
          <div class="kb-upload-section">
            <label class="kb-scope-label">Upload to:</label>
            <select class="kb-scope-select" id="kb-scope-select">
              ${state.ui.activeTab === 'chat' ? `
                <option value="global">Global Knowledge Base</option>
                <option value="conversation">Current Conversation</option>
              ` : `
                <option value="agent">Agent Knowledge (${getAgentName(state.ui.activeTab)})</option>
                <option value="global">Global Knowledge Base</option>
              `}
            </select>
            <button class="kb-upload-btn" data-action="kb-upload">
              ${icons.upload}
              <span>Upload Document</span>
            </button>
          </div>
          <input type="file" id="kb-file-input" class="file-input-hidden" accept="${getAcceptString()}" multiple>
          ${kb.uploading ? `
            <div class="kb-progress">
              <div class="kb-progress-bar" style="width: ${kb.uploadProgress}%"></div>
            </div>
          ` : ''}
          <div class="kb-doc-list">
            ${visibleDocs.length === 0 ? `
              <div class="kb-empty">No documents uploaded</div>
            ` : visibleDocs.map(doc => renderKBDocItem(doc)).join('')}
          </div>
        </div>
      ` : ''}
    </div>
  `;
}

function renderAttachedFiles(state) {
  if (state.attachedFiles.length === 0) return '';

  return `
    <div class="attached-files">
      ${state.attachedFiles.map(file => `
        <div class="attached-file-chip">
          ${icons.file}
          <span class="attached-file-name">${escapeHtml(file.name)}</span>
          <button class="attached-file-remove" data-action="remove-attached" data-file-id="${file.id}">${icons.x}</button>
        </div>
      `).join('')}
    </div>
  `;
}

function renderSourceCitations(message) {
  if (!message.ragSources || message.ragSources.length === 0) return '';

  return `
    <div class="rag-sources">
      <button class="rag-sources-toggle" data-action="toggle-sources" data-msg-id="${message.id}">
        ${icons.database}
        <span>Sources (${message.ragSources.length})</span>
        ${icons.chevronDown}
      </button>
      <div class="rag-sources-list" id="sources-${message.id}" style="display:none">
        ${message.ragSources.map(src => `
          <div class="rag-source-item">
            <div class="rag-source-header">
              ${icons.file}
              <span class="rag-source-name">${escapeHtml(src.docName)}</span>
              <span class="rag-source-chunk">chunk ${src.chunkIndex + 1}/${src.totalChunks}</span>
            </div>
            <div class="rag-source-content">${escapeHtml(src.content.slice(0, 200))}${src.content.length > 200 ? '...' : ''}</div>
          </div>
        `).join('')}
      </div>
    </div>
  `;
}

function formatFileSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function renderModelChips(state) {
  const configuredProviders = providers.getConfigured();
  const configuredIds = new Set(configuredProviders.map(p => p.id));
  const enabledModels = state.models.filter(m => m.enabled && configuredIds.has(m.id));

  if (enabledModels.length === 0) {
    return `<div class="model-chips"><span class="text-muted text-sm">No providers available for parallel mode</span></div>`;
  }

  return `
    <div class="model-chips">
      ${enabledModels.map(model => `
        <button class="model-chip ${state.parallelMode.activeModels.includes(model.id) ? 'selected' : ''}"
                data-action="toggle-model"
                data-model="${model.id}">
          <span class="model-chip-dot" style="background: ${model.color}"></span>
          <span>${model.name}</span>
        </button>
      `).join('')}
    </div>
  `;
}

function bindEvents(container) {
  if (!eventsBound) {
    eventsBound = true;

    // Scroll-to-bottom: use event delegation since chat-area gets re-rendered
    container.addEventListener('scroll', (e) => {
      if (e.target.id === 'chat-area') {
        const scrollBtn = container.querySelector('#scroll-to-bottom');
        if (scrollBtn) {
          const distFromBottom = e.target.scrollHeight - e.target.scrollTop - e.target.clientHeight;
          scrollBtn.style.display = distFromBottom > 200 ? 'flex' : 'none';
        }
      }
    }, true); // useCapture to catch scroll on child elements

    // Provider and model inline selectors
    container.addEventListener('change', (e) => {
      if (e.target.matches('[data-action="change-provider"]')) {
        const providerId = e.target.value;
        const provider = providers.get(providerId);
        if (provider) {
          providers.setActive(providerId, provider.model);
          renderApp(container);
        }
      }
      if (e.target.matches('[data-action="change-model"]')) {
        const active = providers.getActive();
        if (active) {
          providers.setActive(active.providerId, e.target.value);
        }
      }
      if (e.target.matches('[data-action="toggle-kb-doc"]')) {
        toggleDocumentEnabled(e.target.dataset.docId);
      }
    });

    container.addEventListener('click', (e) => {
      // Close any open conversation context menus
      const existingMenu = container.querySelector('.conv-context-menu');
      if (existingMenu && !e.target.closest('.conv-context-menu') && !e.target.closest('[data-action="show-conv-menu"]')) {
        existingMenu.remove();
      }

      // Dismiss mobile message actions on tap elsewhere
      if (!e.target.closest('.msg-actions') && !e.target.closest('.msg-action-btn')) {
        container.querySelectorAll('.message.actions-visible').forEach(el => el.classList.remove('actions-visible'));
      }

      const target = e.target.closest('[data-action]');
      if (!target) return;

      const action = target.dataset.action;

      switch (action) {
        case 'toggle-sidebar':
          toggleSidebar();
          break;
        case 'close-sidebar':
          store.setState({ ui: { sidebarOpen: false } });
          break;
        case 'toggle-parallel':
          toggleParallelMode();
          break;
        case 'new-chat':
          createConversation();
          store.setState({ ui: { sidebarOpen: false } });
          break;
        case 'select-conversation':
          setCurrentConversation(target.dataset.id);
          store.setState({ ui: { sidebarOpen: false } });
          // Update usage display for new conversation
          { const cu = getConversationUsage(target.dataset.id);
            setUsageData({ currentConv: cu, total: store.getState().usage.total }); }
          setFollowUps([]);
          break;
        case 'delete-conversation': {
          e.stopPropagation();
          const id = target.dataset.id;
          deleteConv(id);
          db.deleteConversation(id);
          showToast('Conversation deleted', 'info');
          break;
        }
        case 'pin-conversation': {
          e.stopPropagation();
          handlePinConversation(target.dataset.id);
          break;
        }
        case 'show-conv-menu': {
          e.stopPropagation();
          showConversationMenu(e, target.dataset.id, container);
          break;
        }
        case 'rename-conversation': {
          e.stopPropagation();
          handleRenameConversation(target.dataset.id, container);
          break;
        }
        case 'export-conversation': {
          e.stopPropagation();
          handleExportConversation(target.dataset.id);
          break;
        }
        case 'export-conv-json': {
          e.stopPropagation();
          const cstate = store.getState();
          const cconv = cstate.conversations.find(c => c.id === target.dataset.id);
          if (cconv) {
            const cdata = { title: cconv.title, createdAt: cconv.createdAt, messages: cconv.messages.map(m => ({ role: m.role, content: m.content, model: m.model, timestamp: m.timestamp })) };
            const cblob = new Blob([JSON.stringify(cdata, null, 2)], { type: 'application/json' });
            const curl = URL.createObjectURL(cblob);
            const ca = document.createElement('a');
            ca.href = curl;
            ca.download = `${cconv.title.replace(/[^a-z0-9]/gi, '_').slice(0, 50)}.json`;
            ca.click();
            URL.revokeObjectURL(curl);
            showToast('Exported as JSON', 'success');
          }
          document.querySelector('.conv-context-menu')?.remove();
          break;
        }
        case 'delete-conv-menu': {
          e.stopPropagation();
          const cid = target.dataset.id;
          deleteConv(cid);
          db.deleteConversation(cid);
          showToast('Conversation deleted', 'info');
          break;
        }
        case 'toggle-model': {
          const modelId = target.dataset.model;
          const activeModels = store.getState().parallelMode.activeModels;
          const newModels = activeModels.includes(modelId)
            ? activeModels.filter(id => id !== modelId)
            : [...activeModels, modelId];
          if (newModels.length > 0) {
            store.setState({ parallelMode: { activeModels: newModels } });
          }
          break;
        }
        case 'scroll-to-bottom': {
          const chatArea = container.querySelector('#chat-area');
          if (chatArea) {
            chatArea.scrollTo({ top: chatArea.scrollHeight, behavior: 'smooth' });
          }
          break;
        }
        case 'send':
          handleSend(container);
          break;
        case 'stop': {
          const convId = store.getState().chat.currentConversationId;
          if (convId) stopStream(convId);
          break;
        }
        case 'switch-tab': {
          const tabId = target.dataset.tab;
          const currentInput = container.querySelector('#message-input');
          const currentTab = store.getState().ui.activeTab;
          if (currentInput) {
            tabInputValues[currentTab] = currentInput.value;
          }
          // Stop polling when leaving an agent tab
          if (currentTab !== 'chat' && currentTab !== 'mission-control') {
            stopAgentPolling();
          }
          // Cleanup Mission Control when leaving
          if (currentTab === 'mission-control') {
            cleanupMissionControl();
          }
          setActiveTab(tabId);
          // Load agent transcript history and start polling when switching to an agent tab
          if (tabId !== 'chat' && tabId !== 'mission-control') {
            loadAgentHistory(tabId);
            startAgentPolling(tabId);
          }
          // renderApp is triggered by store subscriber in main.js
          break;
        }

        case 'load-more-history': {
          const agentTabId = target.dataset.agent;
          if (agentTabId) {
            target.textContent = '⏳ Loading...';
            target.disabled = true;
            loadMoreAgentHistory(agentTabId);
          }
          break;
        }
        case 'open-file': {
          const filePath = target.dataset.path;
          if (filePath) openDocViewer(filePath);
          break;
        }
        case 'close-doc-viewer':
          closeDocViewer();
          break;
        case 'settings':
        case 'close-settings':
          settingsOpen = !settingsOpen;
          renderApp(container);
          break;
        case 'quick-prompt': {
          const prompt = target.dataset.prompt;
          const input = container.querySelector('#message-input');
          if (input && prompt) {
            input.value = prompt;
            handleSend(container);
          }
          break;
        }
        case 'export-data':
          handleExportData();
          break;
        case 'clear-data':
          handleClearData(container);
          break;
        case 'logout':
          signOut();
          window.location.reload();
          break;
        case 'copy-code':
          handleCodeCopy(e);
          break;
        case 'copy-message': {
          e.stopPropagation();
          handleCopyMessage(target.dataset.msgId);
          break;
        }
        case 'delete-message': {
          e.stopPropagation();
          handleDeleteMessage(target.dataset.msgId);
          break;
        }
        case 'edit-message': {
          e.stopPropagation();
          handleEditMessage(target.dataset.msgId, container);
          break;
        }
        case 'regenerate-message': {
          e.stopPropagation();
          handleRegenerateMessage(target.dataset.msgId);
          break;
        }
        case 'toggle-voice':
          if (voiceActive) {
            voice.stop();
            voiceActive = false;
          } else {
            voice.start();
            voiceActive = true;
          }
          renderApp(container);
          break;
        case 'open-voice-mode':
          openVoiceMode();
          break;
        case 'download-canvas':
        case 'export-csv':
        case 'clear-form':
        case 'submit-form':
          handleCanvasAction(e);
          break;
        case 'synthesize':
          handleSynthesize();
          break;
        case 'pick-winner': {
          const winnerModel = target.dataset.model;
          voice.speak(`${winnerModel} picked as the best response.`);
          target.closest('.parallel-response-card').style.border = '2px solid var(--accent-success)';
          target.textContent = 'Winner';
          target.disabled = true;
          break;
        }
        case 'attach-file': {
          const fileInput = container.querySelector('#file-input');
          if (fileInput) fileInput.click();
          break;
        }
        case 'remove-attached': {
          removeAttachedFile(target.dataset.fileId);
          break;
        }
        case 'toggle-kb-panel': {
          toggleKBPanel();
          break;
        }
        case 'kb-upload': {
          const kbInput = container.querySelector('#kb-file-input');
          if (kbInput) kbInput.click();
          break;
        }
        case 'delete-kb-doc': {
          e.stopPropagation();
          removeDocument(target.dataset.docId);
          showToast('Document removed', 'info');
          break;
        }
        case 'toggle-sources': {
          const sourcesList = container.querySelector(`#sources-${target.dataset.msgId}`);
          if (sourcesList) {
            const isHidden = sourcesList.style.display === 'none';
            sourcesList.style.display = isHidden ? 'block' : 'none';
            target.classList.toggle('expanded', isHidden);
          }
          break;
        }
        case 'toggle-search':
          updateSettings({ searchEnabled: !store.getState().settings.searchEnabled });
          break;
        case 'use-routing': {
          const routeProvider = target.dataset.provider;
          const routeModel = target.dataset.model;
          providers.setActive(routeProvider, routeModel);
          setRoutingSuggestion(null);
          renderApp(container);
          break;
        }
        case 'dismiss-routing':
          setRoutingSuggestion(null);
          break;
        case 'save-memory': {
          const memContent = target.dataset.content;
          addMemoryItem(memContent);
          setMemorySuggestion(null);
          showToast('Saved to memory', 'success');
          break;
        }
        case 'dismiss-memory':
          setMemorySuggestion(null);
          break;
        case 'follow-up': {
          const followUpText = target.dataset.text;
          if (followUpText) {
            setFollowUps([]);
            sendMessage(followUpText);
          }
          break;
        }
        case 'toggle-search-results': {
          const searchList = container.querySelector(`#search-results-${target.dataset.msgId}`);
          if (searchList) {
            const isHidden = searchList.style.display === 'none';
            searchList.style.display = isHidden ? 'block' : 'none';
            target.classList.toggle('expanded', isHidden);
          }
          break;
        }
        case 'export-md':
          handleExportConversation(store.getState().chat.currentConversationId);
          break;
        case 'export-json':
          handleExportJSON();
          break;
        case 'export-pdf':
          window.print();
          break;
        case 'copy-conversation':
          handleCopyConversation();
          break;
      }
    });

    // Mobile: tap on message to show actions
    container.addEventListener('touchstart', (e) => {
      const msg = e.target.closest('.message');
      if (msg && !msg.classList.contains('streaming')) {
        // Toggle actions visibility on tap
        const wasVisible = msg.classList.contains('actions-visible');
        container.querySelectorAll('.message.actions-visible').forEach(el => el.classList.remove('actions-visible'));
        if (!wasVisible) msg.classList.add('actions-visible');
      }
    }, { passive: true });

    // Keyboard shortcuts
    container.addEventListener('keydown', (e) => {
      const mod = e.metaKey || e.ctrlKey;

      if (mod && e.key === 'k') {
        e.preventDefault();
        settingsOpen = !settingsOpen;
        renderApp(container);
      }
      if (mod && e.key === 'n') {
        e.preventDefault();
        createConversation();
      }
      if (mod && e.key === '/') {
        e.preventDefault();
        const searchInput = container.querySelector('#sidebar-search');
        if (searchInput) {
          store.setState({ ui: { sidebarOpen: true } });
          requestAnimationFrame(() => {
            const si = container.querySelector('#sidebar-search');
            si?.focus();
          });
        }
      }
      if (mod && e.shiftKey && (e.key === 'p' || e.key === 'P')) {
        e.preventDefault();
        toggleParallelMode();
      }
      if (e.key === 'Escape') {
        if (document.getElementById('voice-mode-overlay')) {
          closeVoiceMode();
        } else if (settingsOpen) {
          settingsOpen = false;
          renderApp(container);
        } else if (store.getState().ui.sidebarOpen && window.innerWidth < 768) {
          store.setState({ ui: { sidebarOpen: false } });
        } else {
          const cid = store.getState().chat.currentConversationId;
          if (cid) stopStream(cid);
        }
      }
    });

    // Voice event handling
    voiceUnsubscribe = voice.onEvent((event) => {
      if (event.type === 'input') {
        const inp = container.querySelector('#message-input');
        const btn = container.querySelector('[data-action="send"]');
        if (inp) {
          inp.value = event.transcript;
          if (btn) btn.disabled = false;
        }
      } else if (event.type === 'action') {
        switch (event.action) {
          case 'new-chat': createConversation(); break;
          case 'toggle-parallel': toggleParallelMode(); break;
          case 'settings': settingsOpen = !settingsOpen; renderApp(container); break;
          case 'stop': {
            const cid = store.getState().chat.currentConversationId;
            if (cid) stopStream(cid);
            break;
          }
          case 'read-response': {
            const st = store.getState();
            const conv = st.conversations.find(c => c.id === st.chat.currentConversationId);
            const last = conv?.messages.filter(m => m.role === 'assistant').pop();
            if (last) voice.speak(last.content);
            break;
          }
        }
      } else if (event.type === 'status') {
        voiceActive = event.listening;
        renderApp(container);
      }
    });
  }

  // Per-render bindings
  const input = container.querySelector('#message-input');
  const sendBtn = container.querySelector('[data-action="send"]');

  input?.addEventListener('input', () => {
    const hasValue = input.value.trim().length > 0;
    if (sendBtn) sendBtn.disabled = !hasValue;
    // Smart routing suggestion
    updateRoutingSuggestionForInput(input.value);
  });

  input?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (input.value.trim()) handleSend(container);
    }
  });

  // Sidebar search input
  const searchInput = container.querySelector('#sidebar-search');
  searchInput?.addEventListener('input', (e) => {
    sidebarSearchQuery = e.target.value;
    const listEl = container.querySelector('#conversation-list');
    if (listEl) {
      listEl.innerHTML = renderConversationList(store.getState());
    }
  });

  // File input handlers (per-conversation attachments)
  const fileInput = container.querySelector('#file-input');
  fileInput?.addEventListener('change', async (e) => {
    const files = Array.from(e.target.files);
    for (const file of files) {
      await handleFileUpload(file, false);
    }
    fileInput.value = '';
  });

  // KB file input handler
  const kbFileInput = container.querySelector('#kb-file-input');
  kbFileInput?.addEventListener('change', async (e) => {
    const files = Array.from(e.target.files);
    for (const file of files) {
      await handleFileUpload(file, true);
    }
    kbFileInput.value = '';
  });

  // Drag & drop
  const chatArea = container.querySelector('#chat-area');
  const dropOverlay = container.querySelector('#drop-overlay');
  let dragCounter = 0;

  container.addEventListener('dragenter', (e) => {
    e.preventDefault();
    dragCounter++;
    if (dropOverlay) dropOverlay.classList.add('visible');
  });

  container.addEventListener('dragleave', (e) => {
    e.preventDefault();
    dragCounter--;
    if (dragCounter <= 0) {
      dragCounter = 0;
      if (dropOverlay) dropOverlay.classList.remove('visible');
    }
  });

  container.addEventListener('dragover', (e) => {
    e.preventDefault();
  });

  container.addEventListener('drop', async (e) => {
    e.preventDefault();
    dragCounter = 0;
    if (dropOverlay) dropOverlay.classList.remove('visible');
    const files = Array.from(e.dataTransfer?.files || []);
    for (const file of files) {
      if (isAcceptedFile(file)) {
        await handleFileUpload(file, false);
      } else {
        showToast(`Unsupported: ${file.name}`, 'error');
      }
    }
  });

  requestAnimationFrame(() => input?.focus());
}

// ─────────────────────────────────────────
// MESSAGE ACTIONS
// ─────────────────────────────────────────

function handleCopyMessage(msgId) {
  const state = store.getState();
  const conv = state.conversations.find(c => c.id === state.chat.currentConversationId);
  const msg = conv?.messages.find(m => m.id === msgId);
  // Also check agent conversations
  if (!msg) {
    for (const [, agentConv] of Object.entries(state.agentConversations)) {
      const found = agentConv.messages.find(m => m.id === msgId);
      if (found) {
        navigator.clipboard.writeText(found.content).then(() => showToast('Copied to clipboard', 'success'));
        return;
      }
    }
    return;
  }
  navigator.clipboard.writeText(msg.content).then(() => showToast('Copied to clipboard', 'success'));
}

function handleDeleteMessage(msgId) {
  const state = store.getState();
  const convId = state.chat.currentConversationId;
  const conv = state.conversations.find(c => c.id === convId);
  if (!conv) return;

  const messages = conv.messages.filter(m => m.id !== msgId);
  const conversations = state.conversations.map(c =>
    c.id === convId ? { ...c, messages, updatedAt: Date.now() } : c
  );
  store.setState({ conversations });
  db.saveConversation(conversations.find(c => c.id === convId));
  showToast('Message deleted', 'info');
}

function handleEditMessage(msgId, container) {
  const state = store.getState();
  const convId = state.chat.currentConversationId;
  const conv = state.conversations.find(c => c.id === convId);
  if (!conv) return;

  const msgIndex = conv.messages.findIndex(m => m.id === msgId);
  const msg = conv.messages[msgIndex];
  if (!msg || msg.role !== 'user') return;

  // Replace the message bubble with an editable textarea
  const msgEl = container.querySelector(`[data-msg-id="${msgId}"] .message-body`);
  if (!msgEl) return;

  const bubble = msgEl.closest('.message-bubble');
  bubble.innerHTML = `
    <textarea class="edit-message-textarea">${escapeHtml(msg.content)}</textarea>
    <div class="edit-message-actions">
      <button class="edit-message-btn save" data-edit-save="${msgId}">Save & Send</button>
      <button class="edit-message-btn cancel" data-edit-cancel="${msgId}">Cancel</button>
    </div>
  `;

  const textarea = bubble.querySelector('.edit-message-textarea');
  textarea.focus();
  textarea.setSelectionRange(textarea.value.length, textarea.value.length);

  bubble.querySelector('[data-edit-save]').addEventListener('click', () => {
    const newContent = textarea.value.trim();
    if (!newContent) return;

    // Remove messages after this one, update this message
    const newMessages = conv.messages.slice(0, msgIndex);
    newMessages.push({ ...msg, content: newContent, timestamp: Date.now() });
    const conversations = state.conversations.map(c =>
      c.id === convId ? { ...c, messages: newMessages, updatedAt: Date.now() } : c
    );
    store.setState({ conversations });
    db.saveConversation(conversations.find(c => c.id === convId));

    // Re-send to AI
    sendMessage(newContent);
  });

  bubble.querySelector('[data-edit-cancel]').addEventListener('click', () => {
    renderApp(container);
  });
}

function handleRegenerateMessage(msgId) {
  const state = store.getState();
  const convId = state.chat.currentConversationId;
  const conv = state.conversations.find(c => c.id === convId);
  if (!conv) return;

  const msgIndex = conv.messages.findIndex(m => m.id === msgId);
  if (msgIndex < 0) return;

  // Find the user message before this assistant message
  let userMsg = null;
  for (let i = msgIndex - 1; i >= 0; i--) {
    if (conv.messages[i].role === 'user') {
      userMsg = conv.messages[i];
      break;
    }
  }
  if (!userMsg) return;

  // Remove the assistant message and any after it
  const newMessages = conv.messages.slice(0, msgIndex);
  const conversations = state.conversations.map(c =>
    c.id === convId ? { ...c, messages: newMessages, updatedAt: Date.now() } : c
  );
  store.setState({ conversations });
  db.saveConversation(conversations.find(c => c.id === convId));

  // Re-send the user message
  sendMessage(userMsg.content);
}

// ─────────────────────────────────────────
// CONVERSATION MANAGEMENT
// ─────────────────────────────────────────

function handlePinConversation(convId) {
  const state = store.getState();
  const conversations = state.conversations.map(c =>
    c.id === convId ? { ...c, pinned: !c.pinned, updatedAt: Date.now() } : c
  );
  store.setState({ conversations });
  const conv = conversations.find(c => c.id === convId);
  db.saveConversation(conv);
  showToast(conv.pinned ? 'Conversation pinned' : 'Conversation unpinned', 'info');
}

function showConversationMenu(e, convId, container) {
  // Remove existing menu
  container.querySelector('.conv-context-menu')?.remove();

  const btn = e.target.closest('[data-action="show-conv-menu"]');
  const rect = btn.getBoundingClientRect();

  const menu = document.createElement('div');
  menu.className = 'conv-context-menu';
  menu.innerHTML = `
    <button class="conv-menu-item" data-action="rename-conversation" data-id="${convId}">Rename</button>
    <button class="conv-menu-item" data-action="export-conversation" data-id="${convId}">Export (.md)</button>
    <button class="conv-menu-item" data-action="export-conv-json" data-id="${convId}">Export (.json)</button>
    <button class="conv-menu-item danger" data-action="delete-conv-menu" data-id="${convId}">Delete</button>
  `;
  menu.style.top = `${rect.bottom + 4}px`;
  menu.style.left = `${rect.left}px`;
  container.appendChild(menu);
}

function handleRenameConversation(convId, container) {
  container.querySelector('.conv-context-menu')?.remove();

  const titleEl = container.querySelector(`[data-id="${convId}"] .conversation-title`);
  if (!titleEl) return;

  const state = store.getState();
  const conv = state.conversations.find(c => c.id === convId);
  if (!conv) return;

  const input = document.createElement('input');
  input.type = 'text';
  input.className = 'conv-rename-input';
  input.value = conv.title;
  titleEl.replaceWith(input);
  input.focus();
  input.select();

  const save = () => {
    const newTitle = input.value.trim() || conv.title;
    const conversations = state.conversations.map(c =>
      c.id === convId ? { ...c, title: newTitle, updatedAt: Date.now() } : c
    );
    store.setState({ conversations });
    db.saveConversation(conversations.find(c => c.id === convId));
  };

  input.addEventListener('blur', save);
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') { save(); input.blur(); }
    if (e.key === 'Escape') { input.blur(); }
  });
}

function handleExportConversation(convId) {
  const state = store.getState();
  const conv = state.conversations.find(c => c.id === convId);
  if (!conv) return;

  let md = `# ${conv.title}\n\n`;
  for (const msg of conv.messages) {
    const role = msg.role === 'user' ? 'User' : (msg.model || 'Assistant');
    md += `## ${role}\n\n${msg.content}\n\n---\n\n`;
  }

  const blob = new Blob([md], { type: 'text/markdown' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${conv.title.replace(/[^a-z0-9]/gi, '_').slice(0, 50)}.md`;
  a.click();
  URL.revokeObjectURL(url);
  showToast('Exported as Markdown', 'success');

  // Remove context menu
  document.querySelector('.conv-context-menu')?.remove();
}

function handleExportJSON() {
  const state = store.getState();
  const conv = state.conversations.find(c => c.id === state.chat.currentConversationId);
  if (!conv) return;

  const data = {
    title: conv.title,
    createdAt: conv.createdAt,
    messages: conv.messages.map(m => ({
      role: m.role,
      content: m.content,
      model: m.model,
      timestamp: m.timestamp,
    })),
  };

  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${conv.title.replace(/[^a-z0-9]/gi, '_').slice(0, 50)}.json`;
  a.click();
  URL.revokeObjectURL(url);
  showToast('Exported as JSON', 'success');
}

function handleCopyConversation() {
  const state = store.getState();
  const conv = state.conversations.find(c => c.id === state.chat.currentConversationId);
  if (!conv) return;

  let text = `${conv.title}\n\n`;
  for (const msg of conv.messages) {
    const role = msg.role === 'user' ? 'User' : (msg.model || 'Assistant');
    text += `${role}:\n${msg.content}\n\n`;
  }

  navigator.clipboard.writeText(text).then(() => showToast('Conversation copied', 'success'));
}

// ─────────────────────────────────────────
// SEND / DATA HANDLERS
// ─────────────────────────────────────────

function handleSend(container) {
  const input = container.querySelector('#message-input');
  const message = input?.value.trim();
  if (!message) return;

  const routed = routeInput(message);
  if (routed) {
    input.value = '';
    input.style.height = 'auto';
    if (routed.type === 'help') {
      let conversationId = store.getState().chat.currentConversationId;
      if (!conversationId) {
        createConversation();
        conversationId = store.getState().chat.currentConversationId;
      }
      addMessage({
        conversationId,
        message: {
          role: 'assistant',
          content: routed.message || `**Available Commands:**\n\n${getCommandHelp()}`,
          model: 'System',
          status: 'complete',
        },
      });
    } else if (routed.type === 'action') {
      const actionMap = {
        'new-chat': () => createConversation(),
        'toggle-parallel': () => toggleParallelMode(),
        'settings': () => { settingsOpen = !settingsOpen; renderApp(container); },
        'export-data': () => handleExportData(),
      };
      actionMap[routed.action]?.();
    }
    return;
  }

  input.value = '';
  input.style.height = 'auto';
  const sendBtn = container.querySelector('[data-action="send"]');
  if (sendBtn) sendBtn.disabled = true;

  const state = store.getState();
  tabInputValues[state.ui.activeTab] = '';

  if (state.ui.activeTab !== 'chat') {
    sendAgentMessage(state.ui.activeTab, message);
    return;
  }

  if (state.parallelMode.enabled) {
    sendParallel(message, state.parallelMode.activeModels);
  } else {
    sendMessage(message);
  }
}

function handleSynthesize() {
  const state = store.getState();
  const conv = state.conversations.find(c => c.id === state.chat.currentConversationId);
  if (!conv) return;

  const messages = conv.messages;
  let lastUserMsg = '';
  const parallelResponses = [];

  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role === 'assistant' && messages[i].status === 'complete') {
      parallelResponses.unshift({ model: messages[i].model, content: messages[i].content });
    } else if (messages[i].role === 'user') {
      lastUserMsg = messages[i].content;
      break;
    }
  }

  if (parallelResponses.length >= 2 && lastUserMsg) {
    synthesizeResponses(lastUserMsg, parallelResponses);
  }
}

async function handleFileUpload(file, fromKBPanel) {
  try {
    const state = store.getState();
    const options = {};

    if (fromKBPanel) {
      // Get scope from dropdown
      const scopeSelect = document.querySelector('#kb-scope-select');
      const scope = scopeSelect?.value || 'global';
      options.scope = scope;

      if (scope === 'agent') {
        options.agentId = state.ui.activeTab;
      } else if (scope === 'conversation') {
        options.conversationId = state.chat.currentConversationId;
      }
    } else {
      // Attached files are conversation-scoped
      options.scope = 'conversation';
      options.conversationId = state.chat.currentConversationId;
    }

    const doc = await processFile(file, options);

    if (!fromKBPanel) {
      // Add as attached file for current message
      addAttachedFile({ id: crypto.randomUUID(), name: file.name, docId: doc.id });
    }

    showToast(`${file.name} processed (${doc.chunkCount} chunks)`, 'success');
  } catch (error) {
    showToast(error.message, 'error');
  }
}

async function handleExportData() {
  try {
    const data = await db.exportData();
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `chorus-export-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
    showToast('Data exported', 'success');
  } catch (error) {
    console.error('Export failed:', error);
    showToast('Export failed', 'error');
  }
}

async function handleClearData(container) {
  if (!confirm('This will delete all conversations and settings. Are you sure?')) return;
  await db.clearAll();
  store.setState({
    conversations: [],
    chat: { currentConversationId: null, inputValue: '', isStreaming: false },
  });
  settingsOpen = false;
  renderApp(container);
  showToast('All data cleared', 'info');
}

function setupTextareaAutoResize(textarea) {
  if (!textarea) return;
  textarea.addEventListener('input', () => {
    textarea.style.height = 'auto';
    textarea.style.height = Math.min(textarea.scrollHeight, 200) + 'px';
  });
}

function getAgentName(tabId) {
  const models = providers.get('openclaw')?.models || [];
  const agent = models.find(m => m.id === tabId);
  return agent ? agent.name : tabId;
}

function escapeHtml(text) {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Detect file paths in rendered HTML and make them clickable
 * Matches patterns like /home/ubuntu/..., ~/projects/..., etc.
 */
function linkifyFilePaths(html) {
  // Match file paths in code blocks or plain text
  // Look for paths that look like documents (not binary/images)
  const docExtensions = /\.(md|txt|js|ts|jsx|tsx|css|html|json|yaml|yml|toml|env|sh|py|rs|go|sql|csv|xml|ini|conf|cfg|log|mjs|cjs)$/i;
  const pathPattern = /((?:\/home\/ubuntu|~)\/[^\s<>"'`\])}]+)/g;
  
  return html.replace(pathPattern, (match) => {
    // Only linkify document-like files
    if (docExtensions.test(match)) {
      const escaped = escapeHtml(match);
      return `<span class="file-link" data-action="open-file" data-path="${escaped}" title="Click to view file">${escaped}</span>`;
    }
    return match;
  });
}

// Document viewer state
let _docViewerState = null;

function renderDocViewerOverlay() {
  if (!_docViewerState) return '';
  const { title, content, loading, path } = _docViewerState;
  return `
    <div class="doc-viewer-overlay" data-action="close-doc-viewer">
      <div class="doc-viewer-panel" onclick="event.stopPropagation()">
        <div class="doc-viewer-header">
          <div class="doc-viewer-title">
            <span class="doc-viewer-icon">📄</span>
            <span>${escapeHtml(title)}</span>
          </div>
          <div class="doc-viewer-path">${escapeHtml(path || '')}</div>
          <button class="doc-viewer-close" data-action="close-doc-viewer">✕</button>
        </div>
        <div class="doc-viewer-content markdown-body">
          ${loading ? '<div class="doc-loading">Loading document...</div>' : renderMarkdown(content)}
        </div>
      </div>
    </div>
  `;
}

async function openDocViewer(filePath) {
  const fileName = filePath.split('/').pop();
  _docViewerState = { title: fileName, content: '', loading: true, path: filePath };
  
  // Inject overlay
  const existing = document.querySelector('.doc-viewer-overlay');
  if (existing) existing.remove();
  document.body.insertAdjacentHTML('beforeend', renderDocViewerOverlay());
  
  try {
    // Try VPS first, then local
    let data = await readAgentFile(filePath, 'vps');
    if (!data.ok) {
      data = await readAgentFile(filePath, 'local');
    }
    
    if (data.ok && data.content) {
      _docViewerState = { title: fileName, content: data.content, loading: false, path: filePath };
    } else {
      _docViewerState = { title: fileName, content: `⚠️ Could not load file: ${data.error || 'File not found'}`, loading: false, path: filePath };
    }
  } catch (err) {
    _docViewerState = { title: fileName, content: `⚠️ Error: ${err.message}`, loading: false, path: filePath };
  }
  
  // Update overlay
  const overlay = document.querySelector('.doc-viewer-overlay');
  if (overlay) {
    overlay.outerHTML = renderDocViewerOverlay();
  }
}

function closeDocViewer() {
  _docViewerState = null;
  const overlay = document.querySelector('.doc-viewer-overlay');
  if (overlay) overlay.remove();
}

function formatTime(timestamp) {
  if (!timestamp) return '';
  return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/**
 * Incremental update for streaming messages
 */
export function updateStreamingMessages(container) {
  const state = store.getState();

  if (state.ui.activeTab !== 'chat') {
    const agentConv = state.agentConversations[state.ui.activeTab];
    if (agentConv) {
      const streamingMsgs = agentConv.messages.filter(m => m.status === 'streaming');
      for (const msg of streamingMsgs) {
        const msgEl = container.querySelector(`[data-msg-id="${msg.id}"] .message-body`);
        if (msgEl && msg.content) {
          msgEl.innerHTML = renderMarkdown(msg.content);
        }
      }
    }
  } else {
    const currentConv = state.conversations.find(c => c.id === state.chat.currentConversationId);
    if (!currentConv) return;

    const streamingMsgs = currentConv.messages.filter(m => m.status === 'streaming');
    for (const msg of streamingMsgs) {
      const msgEl = container.querySelector(`[data-msg-id="${msg.id}"] .message-body`);
      if (msgEl && msg.content) {
        msgEl.innerHTML = renderMarkdown(msg.content);
      }
    }
  }

  const chatArea = container.querySelector('#chat-area');
  if (chatArea) {
    chatArea.scrollTop = chatArea.scrollHeight;
  }
}

export default renderApp;
